import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  LinearProgress,
  CircularProgress,
  useTheme,
  alpha,
  Card,
  CardContent,
  useMediaQuery,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Fade,
  Slide,
} from '@mui/material';
import {
  Close as CloseIcon,
  School as SchoolIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  AutoAwesome as AIIcon,
  Psychology as BrainIcon,
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
  Fullscreen as MaximizeIcon,
  FullscreenExit as MinimizeIcon,
  Quiz as QuizIcon,
  Shuffle as ShuffleIcon,
  Timer as TimerIcon,
  TrendingUp as TrendingUpIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

interface MultipleChoiceExerciseModalProps {
  open: boolean;
  onClose: () => void;
  documentContent: string;
}

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation?: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category: string;
  points: number;
}

interface MultipleChoiceExercise {
  id: string;
  title: string;
  description: string;
  questions: QuizQuestion[];
  difficulty: 'easy' | 'medium' | 'hard';
  category: string;
  timeLimit?: number; // in minutes
}

const MultipleChoiceExerciseModal: React.FC<MultipleChoiceExerciseModalProps> = ({
  open,
  onClose,
  documentContent,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [currentExercise, setCurrentExercise] = useState<MultipleChoiceExercise | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [completedExercises, setCompletedExercises] = useState<MultipleChoiceExercise[]>([]);
  const [isMaximized, setIsMaximized] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<{ [questionId: string]: number }>({});
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [quizStarted, setQuizStarted] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [questionResults, setQuestionResults] = useState<{ [questionId: string]: boolean }>({});
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Sample quiz exercises
  const sampleExercises: MultipleChoiceExercise[] = [
    {
      id: 'quiz-1',
      title: 'Professional Skills Assessment',
      description: 'Test your knowledge of essential professional skills and workplace competencies',
      difficulty: 'medium',
      category: 'Professional Development',
      timeLimit: 10,
      questions: [
        {
          id: 'q1',
          question: 'What is the primary purpose of strategic planning in business?',
          options: [
            'To increase employee satisfaction',
            'To set long-term goals and determine the best approach to achieve them',
            'To reduce operational costs immediately',
            'To improve customer service quality'
          ],
          correctAnswer: 1,
          explanation: 'Strategic planning focuses on setting long-term goals and determining the best approach to achieve them, ensuring the organization moves in the right direction.',
          difficulty: 'medium',
          category: 'Strategic Planning',
          points: 10
        },
        {
          id: 'q2',
          question: 'Which leadership style is most effective for team development?',
          options: [
            'Autocratic leadership',
            'Democratic leadership',
            'Laissez-faire leadership',
            'Transformational leadership'
          ],
          correctAnswer: 3,
          explanation: 'Transformational leadership inspires and motivates team members to exceed expectations and develop their potential.',
          difficulty: 'hard',
          category: 'Leadership',
          points: 15
        },
        {
          id: 'q3',
          question: 'What is the key benefit of effective budget management?',
          options: [
            'Increasing employee salaries',
            'Ensuring financial resources are used efficiently',
            'Reducing customer complaints',
            'Improving product quality'
          ],
          correctAnswer: 1,
          explanation: 'Effective budget management ensures financial resources are allocated and used efficiently to achieve organizational goals.',
          difficulty: 'easy',
          category: 'Financial Management',
          points: 5
        },
        {
          id: 'q4',
          question: 'Which factor is most important for maintaining brand consistency?',
          options: [
            'Frequent logo changes',
            'Consistent messaging across all touchpoints',
            'High advertising spend',
            'Multiple brand colors'
          ],
          correctAnswer: 1,
          explanation: 'Brand consistency requires consistent messaging, visual identity, and experience across all customer touchpoints.',
          difficulty: 'medium',
          category: 'Brand Management',
          points: 10
        }
      ]
    },
    {
      id: 'quiz-2',
      title: 'Digital Marketing Fundamentals',
      description: 'Test your understanding of modern digital marketing strategies and tools',
      difficulty: 'hard',
      category: 'Digital Marketing',
      timeLimit: 15,
      questions: [
        {
          id: 'q1',
          question: 'What is the primary goal of SEO (Search Engine Optimization)?',
          options: [
            'To increase social media followers',
            'To improve website visibility in search engine results',
            'To reduce website loading time',
            'To increase email subscribers'
          ],
          correctAnswer: 1,
          explanation: 'SEO aims to improve a website\'s visibility in search engine results pages (SERPs) to attract organic traffic.',
          difficulty: 'easy',
          category: 'SEO',
          points: 5
        },
        {
          id: 'q2',
          question: 'Which metric is most important for measuring email marketing success?',
          options: [
            'Number of emails sent',
            'Open rate and click-through rate',
            'Email list size',
            'Subject line length'
          ],
          correctAnswer: 1,
          explanation: 'Open rate and click-through rate indicate engagement and the effectiveness of your email content.',
          difficulty: 'medium',
          category: 'Email Marketing',
          points: 10
        },
        {
          id: 'q3',
          question: 'What is the main advantage of content marketing?',
          options: [
            'Immediate sales conversion',
            'Building trust and authority with your audience',
            'Reducing marketing costs',
            'Increasing social media followers'
          ],
          correctAnswer: 1,
          explanation: 'Content marketing builds trust and authority by providing valuable information to your audience.',
          difficulty: 'hard',
          category: 'Content Marketing',
          points: 15
        }
      ]
    }
  ];

  useEffect(() => {
    if (open && !currentExercise) {
      setCurrentExercise(sampleExercises[0]);
      setTotalQuestions(sampleExercises[0].questions.length);
    }
  }, [open, currentExercise]);

  // Timer effect
  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0 && quizStarted && !showResults) {
      timerRef.current = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
    } else if (timeRemaining === 0) {
      handleSubmitQuiz();
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [timeRemaining, quizStarted, showResults]);

  const generateNewExercise = async () => {
    setIsGenerating(true);
    
    // Simulate AI generation delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // For now, cycle through sample exercises
    const currentIndex = sampleExercises.findIndex(ex => ex.id === currentExercise?.id);
    const nextIndex = (currentIndex + 1) % sampleExercises.length;
    const newExercise = { ...sampleExercises[nextIndex] };
    
    setCurrentExercise(newExercise);
    setTotalQuestions(newExercise.questions.length);
    setShowResults(false);
    setScore(0);
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setQuizStarted(false);
    setTimeRemaining(null);
    setShowExplanation(false);
    setQuestionResults({});
    setIsGenerating(false);
  };

  const startQuiz = () => {
    setQuizStarted(true);
    if (currentExercise?.timeLimit) {
      setTimeRemaining(currentExercise.timeLimit * 60); // Convert minutes to seconds
    }
  };

  const handleAnswerSelect = (questionId: string, answerIndex: number) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowExplanation(false);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setShowExplanation(false);
    }
  };

  const handleSubmitQuiz = () => {
    if (!currentExercise) return;
    
    let correctCount = 0;
    const results: { [questionId: string]: boolean } = {};
    
    currentExercise.questions.forEach(question => {
      const userAnswer = selectedAnswers[question.id];
      const isCorrect = userAnswer === question.correctAnswer;
      results[question.id] = isCorrect;
      if (isCorrect) correctCount++;
    });
    
    setQuestionResults(results);
    setScore(correctCount);
    setShowResults(true);
    setCompletedExercises(prev => [...prev, currentExercise]);
    
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  const resetExercise = () => {
    setShowResults(false);
    setScore(0);
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setQuizStarted(false);
    setTimeRemaining(null);
    setShowExplanation(false);
    setQuestionResults({});
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'hard': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return '🟢';
      case 'medium': return '🟡';
      case 'hard': return '🔴';
      default: return '⚪';
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreMessage = (score: number, total: number) => {
    const percentage = (score / total) * 100;
    if (percentage >= 90) return { message: 'Outstanding! 🌟', color: '#10b981' };
    if (percentage >= 80) return { message: 'Excellent! 🎉', color: '#10b981' };
    if (percentage >= 70) return { message: 'Good Job! 👍', color: '#f59e0b' };
    if (percentage >= 60) return { message: 'Not Bad! 💪', color: '#f59e0b' };
    return { message: 'Keep Learning! 📚', color: '#ef4444' };
  };

  const currentQuestion = currentExercise?.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const isFirstQuestion = currentQuestionIndex === 0;
  const hasAnswered = currentQuestion && selectedAnswers[currentQuestion.id] !== undefined;
  const isCorrect = currentQuestion && questionResults[currentQuestion.id];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isMaximized ? false : (isMobile ? "sm" : "lg")}
      fullWidth={isMaximized || isMobile}
      fullScreen={isMaximized || isMobile}
      PaperProps={{
        sx: {
          borderRadius: (isMaximized || isMobile) ? 0 : 4,
          background: 'white',
          border: (isMaximized || isMobile) ? 'none' : '3px solid transparent',
          backgroundImage: (isMaximized || isMobile) ? 'none' : 'linear-gradient(white, white), linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'content-box, border-box',
          boxShadow: (isMaximized || isMobile) ? 'none' : '0 20px 60px rgba(236, 72, 153, 0.3), 0 8px 25px rgba(139, 92, 246, 0.2)',
          overflow: 'hidden',
          minHeight: (isMaximized || isMobile) ? '100vh' : '600px',
          maxHeight: (isMaximized || isMobile) ? '100vh' : '90vh',
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        py: isMobile ? 1.5 : 2,
        px: isMobile ? 2 : 4,
        background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
        color: 'white',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: isMobile ? 1 : 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          pointerEvents: 'none',
        }
      }}>
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease',
            position: 'relative',
            zIndex: 2,
            minWidth: isMobile ? 40 : 48,
            minHeight: isMobile ? 40 : 48,
          }}
        >
          <CloseIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
        </IconButton>

        {/* Center Content */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: isMobile ? 1 : 2,
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minWidth: 0,
        }}>
          <Box sx={{
            width: isMobile ? 32 : 40,
            height: isMobile ? 32 : 40,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
            flexShrink: 0,
          }}>
            <QuizIcon sx={{ fontSize: isMobile ? 18 : 22, color: 'white' }} />
          </Box>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            sx={{ 
              fontWeight: 700, 
              fontSize: isMobile ? '1.1rem' : '1.5rem',
              textAlign: isMobile ? 'center' : 'left',
              lineHeight: 1.2,
            }}
          >
            {isMobile ? 'Quiz Exercise' : 'Multiple Choice Quiz'}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: isMobile ? 0.5 : 1,
          flexShrink: 0,
        }}>
          {/* Maximize/Minimize Button - Hide on mobile since it's already fullscreen */}
          {!isMobile && (
            <IconButton
              onClick={() => setIsMaximized(!isMaximized)}
              sx={{
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
                position: 'relative',
                zIndex: 2,
                minWidth: isMobile ? 36 : 40,
                minHeight: isMobile ? 36 : 40,
              }}
              title={isMaximized ? 'Minimize' : 'Maximize'}
            >
              {isMaximized ? <MinimizeIcon sx={{ fontSize: isMobile ? 18 : 20 }} /> : <MaximizeIcon sx={{ fontSize: isMobile ? 18 : 20 }} />}
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ 
        px: isMobile ? 2 : 4, 
        py: isMobile ? 2 : 4,
        background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
        position: 'relative',
        overflow: 'auto',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(236, 72, 153, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
        }
      }}>
        {currentExercise && (
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {/* Exercise Info */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: isMobile ? 1 : 2, 
              mb: isMobile ? 3 : 4,
              flexWrap: isMobile ? 'wrap' : 'nowrap',
            }}>
              <Chip
                label={`${getDifficultyIcon(currentExercise.difficulty)} ${currentExercise.difficulty.toUpperCase()}`}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  backgroundColor: alpha(getDifficultyColor(currentExercise.difficulty), 0.1),
                  color: getDifficultyColor(currentExercise.difficulty),
                  fontWeight: 600,
                  border: `1px solid ${alpha(getDifficultyColor(currentExercise.difficulty), 0.3)}`,
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                }}
              />
              <Chip
                label={currentExercise.category}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  backgroundColor: alpha('#3b82f6', 0.1),
                  color: '#3b82f6',
                  fontWeight: 600,
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                }}
              />
              <Chip
                label={`${currentExercise.questions.length} Questions`}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  backgroundColor: alpha('#8b5cf6', 0.1),
                  color: '#8b5cf6',
                  fontWeight: 600,
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                }}
              />
              {currentExercise.timeLimit && (
                <Chip
                  icon={<TimerIcon />}
                  label={`${currentExercise.timeLimit} min`}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    backgroundColor: alpha('#f59e0b', 0.1),
                    color: '#f59e0b',
                    fontWeight: 600,
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                  }}
                />
              )}
            </Box>

            {/* Exercise Title & Description */}
            <Paper
              elevation={2}
              sx={{
                p: isMobile ? 2 : 3,
                mb: isMobile ? 3 : 4,
                borderRadius: 3,
                background: 'white',
                border: '2px solid rgba(236, 72, 153, 0.1)',
              }}
            >
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: '#1f2937',
                mb: 1,
                fontSize: isMobile ? '1.2rem' : '1.5rem',
              }}>
                {currentExercise.title}
              </Typography>
              <Typography variant="body1" sx={{ 
                color: '#6b7280',
                fontSize: isMobile ? '0.9rem' : '1rem',
                lineHeight: 1.6,
              }}>
                {currentExercise.description}
              </Typography>
            </Paper>

            {/* Timer */}
            {timeRemaining !== null && quizStarted && !showResults && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 3,
                    background: timeRemaining < 60 
                      ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(248, 113, 113, 0.1) 100%)'
                      : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(96, 165, 250, 0.1) 100%)',
                    border: `2px solid ${timeRemaining < 60 ? 'rgba(239, 68, 68, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <TimerIcon sx={{ 
                      color: timeRemaining < 60 ? '#ef4444' : '#3b82f6',
                      fontSize: 20 
                    }} />
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700,
                      color: timeRemaining < 60 ? '#ef4444' : '#3b82f6',
                    }}>
                      {formatTime(timeRemaining)}
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            )}

            {/* Quiz Content */}
            {!quizStarted ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 3 : 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '2px solid rgba(139, 92, 246, 0.1)',
                    textAlign: 'center',
                  }}
                >
                  <Box sx={{ mb: 3 }}>
                    <QuizIcon sx={{ 
                      fontSize: isMobile ? 48 : 64, 
                      color: '#8b5cf6',
                      mb: 2 
                    }} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    color: '#1f2937',
                    mb: 2,
                    fontSize: isMobile ? '1.5rem' : '2rem',
                  }}>
                    Ready to Start?
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: '#6b7280',
                    mb: 3,
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    lineHeight: 1.6,
                  }}>
                    This quiz contains {totalQuestions} questions. 
                    {currentExercise.timeLimit && ` You have ${currentExercise.timeLimit} minutes to complete it.`}
                    <br />
                    Take your time and choose the best answer for each question.
                  </Typography>
                  <Button
                    onClick={startQuiz}
                    variant="contained"
                    size="large"
                    startIcon={<QuizIcon />}
                    sx={{
                      borderRadius: 3,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      fontSize: isMobile ? '1rem' : '1.1rem',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)',
                      },
                    }}
                  >
                    Start Quiz
                  </Button>
                </Paper>
              </motion.div>
            ) : !showResults ? (
              <motion.div
                key={currentQuestionIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 3 : 4,
                    mb: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '2px solid rgba(139, 92, 246, 0.1)',
                  }}
                >
                  {/* Question Header */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 1,
                  }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600,
                      color: '#6b7280',
                      fontSize: isMobile ? '0.9rem' : '1rem',
                    }}>
                      Question {currentQuestionIndex + 1} of {totalQuestions}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip
                        label={`${getDifficultyIcon(currentQuestion?.difficulty || 'medium')} ${currentQuestion?.difficulty?.toUpperCase() || 'MEDIUM'}`}
                        size="small"
                        sx={{
                          backgroundColor: alpha(getDifficultyColor(currentQuestion?.difficulty || 'medium'), 0.1),
                          color: getDifficultyColor(currentQuestion?.difficulty || 'medium'),
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                      <Chip
                        label={`${currentQuestion?.points || 10} pts`}
                        size="small"
                        sx={{
                          backgroundColor: alpha('#8b5cf6', 0.1),
                          color: '#8b5cf6',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Question */}
                  <Typography variant="h5" sx={{ 
                    fontWeight: 700, 
                    color: '#1f2937',
                    mb: 3,
                    fontSize: isMobile ? '1.1rem' : '1.3rem',
                    lineHeight: 1.4,
                  }}>
                    {currentQuestion?.question}
                  </Typography>

                  {/* Options */}
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <RadioGroup
                      value={selectedAnswers[currentQuestion?.id || ''] !== undefined ? selectedAnswers[currentQuestion?.id || ''].toString() : ''}
                      onChange={(e) => handleAnswerSelect(currentQuestion?.id || '', parseInt(e.target.value))}
                    >
                      {currentQuestion?.options.map((option, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.1 }}
                        >
                          <FormControlLabel
                            value={index.toString()}
                            control={
                              <Radio
                                sx={{
                                  color: '#8b5cf6',
                                  '&.Mui-checked': {
                                    color: '#8b5cf6',
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography sx={{ 
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                fontWeight: 500,
                                color: '#374151',
                                lineHeight: 1.5,
                              }}>
                                {option}
                              </Typography>
                            }
                            sx={{
                              mb: 2,
                              p: 2,
                              borderRadius: 2,
                              border: '2px solid transparent',
                              background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                border: '2px solid rgba(139, 92, 246, 0.2)',
                                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(139, 92, 246, 0.02) 100%)',
                              },
                              '&.Mui-checked': {
                                border: '2px solid #8b5cf6',
                                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
                              },
                            }}
                          />
                        </motion.div>
                      ))}
                    </RadioGroup>
                  </FormControl>

                  {/* Navigation */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mt: 4,
                    flexWrap: 'wrap',
                    gap: 2,
                  }}>
                    <Button
                      onClick={handlePreviousQuestion}
                      disabled={isFirstQuestion}
                      variant="outlined"
                      size={isMobile ? 'small' : 'medium'}
                      sx={{
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: 1,
                        borderColor: '#e2e8f0',
                        color: '#64748b',
                        '&:hover': {
                          borderColor: '#cbd5e1',
                          backgroundColor: '#f8fafc',
                        },
                        '&:disabled': {
                          borderColor: '#f1f5f9',
                          color: '#cbd5e1',
                        },
                      }}
                    >
                      Previous
                    </Button>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {Array.from({ length: totalQuestions }, (_, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: i === currentQuestionIndex 
                              ? '#8b5cf6' 
                              : i < currentQuestionIndex 
                                ? '#10b981' 
                                : '#e2e8f0',
                            transition: 'all 0.3s ease',
                          }}
                        />
                      ))}
                    </Box>

                    {isLastQuestion ? (
                      <Button
                        onClick={handleSubmitQuiz}
                        disabled={!hasAnswered}
                        variant="contained"
                        size={isMobile ? 'small' : 'medium'}
                        startIcon={<CheckIcon />}
                        sx={{
                          borderRadius: 3,
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 3,
                          py: 1,
                          background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                          },
                          '&:disabled': {
                            backgroundColor: '#e2e8f0',
                            color: '#94a3b8',
                          },
                        }}
                      >
                        Submit Quiz
                      </Button>
                    ) : (
                      <Button
                        onClick={handleNextQuestion}
                        disabled={!hasAnswered}
                        variant="contained"
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                          borderRadius: 3,
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 3,
                          py: 1,
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)',
                          },
                          '&:disabled': {
                            backgroundColor: '#e2e8f0',
                            color: '#94a3b8',
                          },
                        }}
                      >
                        Next
                      </Button>
                    )}
                  </Box>
                </Paper>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 3 : 4,
                    mb: 3,
                    borderRadius: 3,
                    background: score === totalQuestions 
                      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(34, 197, 94, 0.1) 100%)'
                      : score > totalQuestions / 2
                      ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%)'
                      : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(248, 113, 113, 0.1) 100%)',
                    border: `2px solid ${score === totalQuestions 
                      ? 'rgba(16, 185, 129, 0.3)'
                      : score > totalQuestions / 2
                      ? 'rgba(245, 158, 11, 0.3)'
                      : 'rgba(239, 68, 68, 0.3)'}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                    {score === totalQuestions ? (
                      <TrophyIcon sx={{ fontSize: isMobile ? 40 : 48, color: '#10b981' }} />
                    ) : (
                      <TrendingUpIcon sx={{ 
                        fontSize: isMobile ? 40 : 48, 
                        color: score > totalQuestions / 2 ? '#f59e0b' : '#ef4444' 
                      }} />
                    )}
                    <Typography variant="h3" sx={{ 
                      fontWeight: 700, 
                      color: score === totalQuestions 
                        ? '#10b981'
                        : score > totalQuestions / 2
                        ? '#f59e0b'
                        : '#ef4444'
                    }}>
                      {score}/{totalQuestions}
                    </Typography>
                  </Box>
                  
                  <Typography variant="h4" sx={{ 
                    textAlign: 'center', 
                    fontWeight: 700,
                    color: score === totalQuestions 
                      ? '#10b981'
                      : score > totalQuestions / 2
                      ? '#f59e0b'
                      : '#ef4444',
                    mb: 2,
                    fontSize: isMobile ? '1.5rem' : '2rem',
                  }}>
                    {getScoreMessage(score, totalQuestions).message}
                  </Typography>
                  
                  <Typography variant="h6" sx={{ 
                    textAlign: 'center', 
                    fontWeight: 600,
                    color: '#6b7280',
                    mb: 3,
                  }}>
                    {Math.round((score / totalQuestions) * 100)}% Correct
                  </Typography>

                  {/* Question Review */}
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      color: '#1f2937',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      <LightbulbIcon sx={{ fontSize: 20, color: '#8b5cf6' }} />
                      Question Review
                    </Typography>
                    
                    {currentExercise.questions.map((question, index) => (
                      <motion.div
                        key={question.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <Card
                          sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            border: `2px solid ${questionResults[question.id] ? '#10b981' : '#ef4444'}`,
                            background: questionResults[question.id] 
                              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(34, 197, 94, 0.05) 100%)'
                              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(248, 113, 113, 0.05) 100%)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            {questionResults[question.id] ? (
                              <CheckIcon sx={{ fontSize: 20, color: '#10b981' }} />
                            ) : (
                              <ErrorIcon sx={{ fontSize: 20, color: '#ef4444' }} />
                            )}
                            <Typography variant="subtitle1" sx={{ 
                              fontWeight: 600,
                              color: questionResults[question.id] ? '#10b981' : '#ef4444',
                            }}>
                              Question {index + 1}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ 
                            color: '#6b7280',
                            mb: 1,
                          }}>
                            {question.question}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: '#374151',
                            fontWeight: 500,
                          }}>
                            Your answer: {question.options[selectedAnswers[question.id] || 0]}
                          </Typography>
                          {!questionResults[question.id] && (
                            <Typography variant="body2" sx={{ 
                              color: '#10b981',
                              fontWeight: 500,
                              mt: 0.5,
                            }}>
                              Correct answer: {question.options[question.correctAnswer]}
                            </Typography>
                          )}
                        </Card>
                      </motion.div>
                    ))}
                  </Box>
                </Paper>
              </motion.div>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ 
        p: isMobile ? 2 : 4, 
        pt: isMobile ? 1 : 2,
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderTop: '1px solid rgba(236, 72, 153, 0.1)',
        position: 'relative',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(236, 72, 153, 0.3) 50%, transparent 100%)',
        }
      }}>
        <Box sx={{ 
          display: 'flex', 
          gap: isMobile ? 1 : 2, 
          width: '100%', 
          justifyContent: isMobile ? 'center' : 'space-between',
          flexDirection: isMobile ? 'column' : 'row',
        }}>
          <Box sx={{ 
            display: 'flex', 
            gap: isMobile ? 1 : 2,
            flexDirection: isMobile ? 'row' : 'row',
            justifyContent: isMobile ? 'center' : 'flex-start',
          }}>
            <Button
              onClick={resetExercise}
              variant="outlined"
              disabled={!currentExercise || isGenerating}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                px: isMobile ? 2 : 3,
                py: isMobile ? 1 : 1.5,
                borderColor: '#e2e8f0',
                color: '#64748b',
                fontSize: isMobile ? '0.875rem' : '1rem',
                '&:hover': {
                  borderColor: '#cbd5e1',
                  backgroundColor: '#f8fafc',
                },
              }}
            >
              Reset
            </Button>
            
            <Button
              onClick={generateNewExercise}
              variant="contained"
              disabled={isGenerating}
              size={isMobile ? 'small' : 'medium'}
              startIcon={isGenerating ? <CircularProgress size={isMobile ? 16 : 20} /> : <ShuffleIcon />}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                px: isMobile ? 2 : 3,
                py: isMobile ? 1 : 1.5,
                fontSize: isMobile ? '0.875rem' : '1rem',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)',
                },
                '&:disabled': {
                  backgroundColor: '#e2e8f0',
                  color: '#94a3b8',
                },
              }}
            >
              {isGenerating ? (isMobile ? 'Generating...' : 'Generating...') : (isMobile ? 'New Quiz' : 'Generate New Quiz')}
            </Button>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            gap: isMobile ? 1 : 2,
            justifyContent: isMobile ? 'center' : 'flex-end',
          }}>
            <Button
              onClick={onClose}
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                px: isMobile ? 3 : 4,
                py: isMobile ? 1 : 1.5,
                fontSize: isMobile ? '0.875rem' : '1rem',
                background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default MultipleChoiceExerciseModal;
